import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/shadcn/table'
import { TableCard } from '@/components/common/table/TableCard'
import { PaginationFooter } from '@/components/common/table/PaginationFooter'
import { LoaderTable } from '@/components/common/loaders/LoaderTable'
import { SortableTableHead } from '@/components/common/SortableTableHead'
import { DownloadButton } from '@/components/common/DownloadButton'
import { useGetApiCompaniesCurrentShopmodulesFullpayoutShopModuleIdPayouts } from '@/api/generated/company-management-user/company-management-user'
import { OrderType } from '@/api/model/orderType'

const DEFAULT_PAGE_SIZE = 20

// Sort keys the backend accepts (`GetFullPayoutBatchesLogic`): the date column sorts
// on `payoutTimestamp`, not on the model's `payoutDate`.
const SORT_DATE = 'payoutTimestamp'
const SORT_TOTAL_POINTS = 'totalPoints'

function formatDate(ts: string) {
  return new Date(ts.endsWith('Z') ? ts : ts + 'Z').toLocaleDateString('de-DE', {
    day: '2-digit', month: '2-digit', year: 'numeric',
  })
}

interface FullPayoutsTableProps {
  shopModuleId: number
}

export function FullPayoutsTable({ shopModuleId }: FullPayoutsTableProps) {
  const { t } = useTranslation(['shop-modules-fullpayout', 'common'])
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE)
  const [sort, setSort] = useState<string | undefined>(SORT_DATE)
  const [order, setOrder] = useState<OrderType | undefined>(OrderType.Descending)

  const handleSort = (field: string | undefined, ord: OrderType | undefined) => {
    setSort(field)
    setOrder(ord)
    setPage(1)
  }

  const { data, isLoading, isError } = useGetApiCompaniesCurrentShopmodulesFullpayoutShopModuleIdPayouts(
    shopModuleId,
    {
      Offset: (page - 1) * pageSize,
      Limit: pageSize,
      Sort: sort,
      Order: order,
    },
    // A non-numeric :shopModuleId would produce a request for `/NaN/payouts`.
    { query: { enabled: Number.isInteger(shopModuleId) } },
  )
  const raw = (data as any)?.data ?? data
  const items = raw?.items ?? []
  const totalCount: number = raw?.totalCount ?? 0

  return (
    <TableCard>
      <div className="flex-1 overflow-auto scrollbar-custom relative">
        <Table>
          <TableHeader className="sticky top-0 z-10 bg-card shadow-sm [&_th]:bg-card">
            <TableRow>
              <SortableTableHead field={SORT_DATE} currentSort={sort} currentOrder={order} onSort={handleSort}>{t('columns.date')}</SortableTableHead>
              <SortableTableHead field={SORT_TOTAL_POINTS} currentSort={sort} currentOrder={order} onSort={handleSort}>{t('columns.totalPoints')}</SortableTableHead>
              <TableHead className="w-[80px]">{t('columns.download')}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            <LoaderTable
              isLoading={isLoading}
              isError={isError}
              isEmpty={items.length === 0}
              colSpan={3}
              errorMessage={t('table.loadError')}
              emptyMessage={t('table.empty')}
            >
              {items.map((item: any) => (
              <TableRow key={item.id}>
                <TableCell className="font-medium">{item.payoutDate ? formatDate(item.payoutDate) : '—'}</TableCell>
                <TableCell>{item.totalPoints != null ? item.totalPoints.toLocaleString('de-DE') : '—'}</TableCell>
                <TableCell><DownloadButton url={item.pdfDownloadUrl ?? null} /></TableCell>
              </TableRow>
              ))}
            </LoaderTable>
          </TableBody>
        </Table>
      </div>
      <PaginationFooter
        offset={(page - 1) * pageSize}
        limit={pageSize}
        totalCount={totalCount}
        onOffsetChange={(offset) => setPage(Math.floor(offset / pageSize) + 1)}
        onPageSizeChange={(size) => { setPageSize(size); setPage(1) }}
      />
    </TableCard>
  )
}
